/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { faro, type LogLevel as FaroLogLevel } from "@grafana/faro-web-sdk";
import { Span, SpanContext } from "@opentelemetry/api";
import { faroManager } from "./faro-manager";

type LogLevel = "trace" | "debug" | "info" | "warn" | "error";

// Function to safely serialize objects for logging
const safeSerialize = (obj: any): any => {
	const seen = new WeakSet();
	try {
		return JSON.parse(
			JSON.stringify(obj, (key, value) => {
				if (typeof value === "object" && value !== null) {
					if (seen.has(value)) {
						return "[Circular Reference]";
					}
					seen.add(value);
				}
				return value;
			}),
		);
	} catch {
		// If serialization fails, convert to string
		return String(obj);
	}
};

// Function to get current trace context
const getTraceContext = (): Record<string, any> => {
	if (!faro.api?.getOTEL?.()) {
		return {};
	}

	const otel = faro.api.getOTEL();
	if (!otel?.trace) {
		return {};
	}

	let span: Span | undefined = otel.trace.getActiveSpan();

	// If no active span exists, create a new one
	if (!span) {
		const tracer = otel.trace.getTracer("client-logger");
		span = tracer.startSpan("client-log");
	}

	const spanContext: SpanContext = span.spanContext();
	return {
		// OpenTelemetry trace context format using standard semantic conventions
		"trace.id": spanContext.traceId,
		"span.id": spanContext.spanId,
		"trace.flags": spanContext.traceFlags.toString(16).padStart(2, "0"),
	};
};

// Simple client-side logger that's safe to use in the browser
const createClientLogger = (context: Record<string, any> = {}) => {
	const isDevelopment =
		process.env.NEXT_PUBLIC_NODE_ENV === "development" ||
		process.env.NEXT_PUBLIC_NODE_ENV === "test";
	const forceProductionLogging =
		process.env.NEXT_PUBLIC_FORCE_PRODUCTION_LOGGING === "true";
	const shouldSendToFaro = !isDevelopment || forceProductionLogging;

	const pushLog = (
		level: LogLevel,
		message: string,
		attributes: Record<string, any> = {},
	) => {
		const traceContext = getTraceContext();
		const timestamp = new Date().toISOString();

		// Safely serialize all attributes
		const safeAttributes = safeSerialize({
			...context,
			...attributes,
		});

		// Format for console logging
		const consoleContext = {
			...traceContext,
			...safeAttributes,
			timestamp,
		};

		// Development mode: console only
		if (!shouldSendToFaro) {
			const colors = {
				trace: "\x1b[90m",
				debug: "\x1b[90m",
				info: "\x1b[36m",
				warn: "\x1b[33m",
				error: "\x1b[31m",
			};
			console[level](
				colors[level],
				`[${level.toUpperCase()}]`,
				message,
				consoleContext,
			);
			return;
		}

		// Production mode or forced production logging: send to Faro in OTEL format
		if (faro.api) {
			const logContext = {
				...safeAttributes,
				timestamp,
				// OTEL attributes format
				"service.name":
					process.env.NEXT_PUBLIC_FARO_APP_NAME || "unknown_service:webjs",
				"service.namespace": process.env.NEXT_PUBLIC_FARO_APP_NAMESPACE,
				"service.version": "1.0.0",
				"service.environment": process.env.NODE_ENV || "development",
				// OTEL trace context
				...traceContext,
			};

			// console.log('logContext', logContext);

			// Queue the log if Faro isn't ready yet
			faroManager.queueLog(() => {
				// Map our log levels to Faro's expected levels and handle undefined case
				// const faroLevel = level === 'trace' ? 'debug' : level;
				faro.api?.pushLog([message], {
					level: level as FaroLogLevel,
					context: logContext,
				});
			});
		}
	};

	return {
		trace: (message: string, attributes: Record<string, any> = {}) => {
			pushLog("trace", message, attributes);
		},
		debug: (message: string, attributes: Record<string, any> = {}) => {
			pushLog("debug", message, attributes);
		},
		info: (message: string, attributes: Record<string, any> = {}) => {
			pushLog("info", message, attributes);
		},
		warn: (message: string, attributes: Record<string, any> = {}) => {
			pushLog("warn", message, attributes);
		},
		error: (
			message: string,
			error?: Error,
			attributes: Record<string, any> = {},
		) => {
			const errorData = error
				? {
						"error.message": error.message,
						"error.stack": error.stack,
						"error.type": error.name,
					}
				: {};
			pushLog("error", message, { ...attributes, ...errorData });
		},
		child: (childContext: Record<string, any>) => {
			return createClientLogger({
				...context,
				...childContext,
			});
		},
	};
};

// Create the default logger that automatically includes trace context
export const clientLogger = createClientLogger();
